import React from 'react';
import { Box, Typography } from '@mui/material';
import InlineConverter from './InlineConverter';
import SmartNumberDisplay from './SmartNumberDisplay';
import { detectDataType, isDecimalNumber } from '../../utils/hexConverter';

interface SmartHexDisplayProps {
    value: string;
    label?: string;
    functionName?: string;
    contractContext?: {
        name?: string;
        symbol?: string;
        decimals?: number;
    };
    showInlineConversion?: boolean;
    compactConversion?: boolean;
    onOpenFloatingConverter?: (value: string) => void;
}

/**
 * Enhanced component that displays a value and automatically adds inline conversion
 * for both hex data and decimal numbers
 */
const SmartHexDisplay: React.FC<SmartHexDisplayProps> = ({
    value,
    label,
    functionName,
    contractContext,
    showInlineConversion = true,
    compactConversion = true,
    onOpenFloatingConverter
}) => {
    // Check if the value is a decimal number
    const isDecimal = isDecimalNumber(value);

    // Check if the value looks like convertible hex data
    const dataType = detectDataType(value);
    const isHexConvertible = dataType !== 'unknown' && value.startsWith('0x');

    return (
        <Box>
            {/* Original Value */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography
                    variant="body2"
                    sx={{
                        fontFamily: 'monospace',
                        wordBreak: 'break-all',
                        flex: 1,
                        minWidth: 0
                    }}
                >
                    {value}
                </Typography>

                {/* Smart Number Display for decimal numbers */}
                {showInlineConversion && isDecimal && (
                    <SmartNumberDisplay
                        value={value}
                        label={label}
                        functionName={functionName}
                        contractContext={contractContext}
                        compact={compactConversion}
                        onOpenFloatingConverter={onOpenFloatingConverter}
                    />
                )}

                {/* Inline Hex Conversion for hex values */}
                {showInlineConversion && !isDecimal && isHexConvertible && (
                    <InlineConverter
                        value={value}
                        label={label}
                        compact={compactConversion}
                        onOpenFloatingConverter={onOpenFloatingConverter}
                    />
                )}
            </Box>

            {/* Full Conversion Details (non-compact mode) */}
            {showInlineConversion && !compactConversion && (
                <>
                    {/* For decimal numbers */}
                    {isDecimal && (
                        <SmartNumberDisplay
                            value={value}
                            label={label}
                            functionName={functionName}
                            contractContext={contractContext}
                            compact={false}
                            showRawValue={false}
                            onOpenFloatingConverter={onOpenFloatingConverter}
                        />
                    )}

                    {/* For hex values */}
                    {!isDecimal && isHexConvertible && (
                        <InlineConverter
                            value={value}
                            label={label}
                            compact={false}
                            showRawValue={false}
                            onOpenFloatingConverter={onOpenFloatingConverter}
                        />
                    )}
                </>
            )}
        </Box>
    );
};

export default SmartHexDisplay;
